import { Action, Dispatch, MiddlewareAPI } from 'redux';
import { ITSUser, Name } from '../../types/user';
import { AreArraysEqual, getRoleFromClaims, noop, randomElement } from '../../utils/utils';
import {
  firebaseInitialized,
  initAuthUnsubscribeAction,
  signOut,
  updateAuthUserAction,
  updateCurrentUser,
  updateCurrentUserUnsubscribe,
} from '../actions/firebase-actions';
import { AuthUser } from '../reducers/firebase-reducer';
import { State } from '../state';
import { FIREBASE, Firebase } from '../../remote/firebase';
import { getClassRoomContext, getLTIAuthToken, getRoleFromLTIRoles, sendCompletionProgress } from '../../remote/lti';
import { addTour, UILocation } from '../reducers/app-reducers';
import {
  appStart,
  resetProgressForModule,
  setClassAutoGrade,
  setClassStartAndEndDates,
  setCurrentClassroomToken,
  setDashboardTelemetry,
  setLocation,
  setLTIEnv,
  setLTIModule,
  setToursShown,
  setTourSteps,
  updateClassSettingsFromDb,
} from '../actions/app-actions';
import { Classroom, TeacherClassroom } from '../../types/classroom';
import { isTeacher, ROLES } from '../../constants/roles';
import { ltiTourSteps, tourSteps } from '../../components/tour-steps';
import { APP_CFG } from '../../../src/utils/appcfg';
import { studentCompletedModule, studentProblemResult, studentSurveyClicked } from '../actions/student-client-results';
import {
  addNewEventForExample, addNewEvents, addNewResults, exampleToDisplayGenerated,
  generateExampleFromEventId, getModuleCompletionForUser, loadAllEventsForUser, loadAndDisplayExampleFromEventId,
  loadStudentDataForDashboard, pickThenLoadExampleFromEventIds,
  saveNewExampleSelectionInformation,
  setCurrentCompetencyId,
  setModuleCompletionForUser,
  updateRandomExampleSettingsForCardId } from '../actions/statistics';
import { Claims, ProblemGenerator, RawEvalPageData, RawPageData, SFFProblemType } from '../../../../its-pages/src';
import { dashboardSettingsLoaded, saveCardSettings, setCardType } from '../actions/teacher-ui-actions';
import { FirebaseCardType } from '../../types/cards';
import { readUserLocalStorage } from '../../constants/local-storage';
import { launchBugReport, launchEvaluateSelfEvalResponse, loadMoreSelfEvalResponse, loadedBugReports, loadedSelfEvalResponses, updateBugReport, updateSelfEvalResponse } from '../actions/admin-ui-actions';

function createClassTokenFromClaims(claims: Claims | undefined) {
  if (claims && claims.context) {
    const { context: { id, platformClientId: clientId, platformIss: iss }} = claims;
    const classToken = `${iss}-${clientId}-${id}`;
    const ret = classToken.replaceAll('/', '-');
    return ret;
  }
  return '';
}

function subscribeCurrentUser<S extends State>(
  store: MiddlewareAPI<Dispatch, S>,
  firebase: Firebase,
  authUser: AuthUser
) {
  try {
    if (authUser?.firebaseUser) {
      const unsubscribe = firebase
        .user(authUser.firebaseUser.uid)
        .onSnapshot(async (snapshot) => {
          const claims = authUser.claims;
          const state = store.getState();
          const isLTI = state.app.ltiEnv;
          const classToken = createClassTokenFromClaims(claims);
          const module = claims?.context?.module;
          const currentUser = state.firebase?.currentUser;
          console.log(classToken);
  
          if (snapshot.exists) {
            const user = snapshot.data() as unknown as ITSUser;
            const sameUser = user?.uid === currentUser?.uid;
            if (APP_CFG.showTourOnStart) {
              const initialShown = user.toursShown && user.toursShown.indexOf('initial') >= 0
              const showTourOnOpen = 
                !user.toursShown || 
                !initialShown || 
                user.toursShown.indexOf('start-up') < 0;
              
              if (!initialShown) {
                const toursShown = user.toursShown || [];
                store.dispatch(setToursShown(addTour('initial', toursShown)));
              }

              if (showTourOnOpen && !sameUser)
              {
                setTimeout(()=>{
                  store.dispatch(setTourSteps( 
                    state.app.ltiEnv ? ltiTourSteps: tourSteps
                  ));
                }, 500);
              }
              //update legacy teacher structure
              if (!isLTI && isTeacher(getRoleFromClaims(claims)) && !user.teacherClasses) {
                firebase.updateUser(user, {teacherClasses: user.classrooms});
                user.teacherClasses = user.classrooms;
                user.classrooms = {};
              } 
            }
            store.dispatch(updateCurrentUser(user));
            if (isLTI) {
              if (claims) {
                const role = getRoleFromClaims(claims) || ROLES.STUDENT;

                if (isTeacher(role)) {
                  const className = claims?.context?.className;
                  const teacherClassroom: TeacherClassroom = {
                    name: className,
                    token: classToken,
                    students: {},
                    activeLessons: { ITS2021: [] },
                  };
                  await firebase.ensureLTIClassroom({ user, teacherClassroom });
                }
                store.dispatch(setCurrentClassroomToken(classToken));
                if (module) {
                  store.dispatch(setLTIModule(module));
                }
              }
            } else {
              handleStandaloneVersionLoadClassFromUser(user, store);
            }
          } else {
            // User does not exist in firebase
            if (isLTI) {
              if (claims) {
                const uid = claims?.user_id;
                const role = getRoleFromClaims(claims) || ROLES.STUDENT;
                const username = uid;
                const name: Name = { given: '', surname: ''}; // Avoid saving personal info from canvas
                const className = claims?.context?.className || '';
                const classroom: Classroom = {
                  name: className,
                  token: classToken,
                };

                let user: ITSUser | undefined;
                if (isTeacher(role)) {
                  user = await firebase.ensureLTITeacher(
                    uid,
                    username,
                    name,
                    classroom,
                  );
                } else {
                  user = await firebase.ensureLTIStudent(
                    uid,
                    username,
                    name,
                    classroom,
                  );
                }

                store.dispatch(updateCurrentUser(user));
                store.dispatch(setCurrentClassroomToken(classToken));
                if (module) {
                  store.dispatch(setLTIModule(module));
                }
              }
            } else {
              if (currentUser) {
                store.dispatch(updateCurrentUser(undefined));
              }
            }
          }
        });

      store.dispatch(updateCurrentUserUnsubscribe(unsubscribe));
    }
  } catch (e) {
    console.log(e);
    // keep the return just in case more code is added later
    // eslint-disable-next-line no-useless-return
    return;
  }
}

function handleStandaloneVersionLoadClassFromUser(payload: ITSUser, store: MiddlewareAPI<Dispatch, State>) {
  const { classrooms, uid } = payload;
  const { currentClassroomToken } = store.getState().app;

  // Set class code to whatever is saved in local storage
  const savedToken = readUserLocalStorage(uid, 'currentClassroomToken');
  if (savedToken) {
    if (savedToken === currentClassroomToken) {
      return;
    }
    if (savedToken.length > 0) {
      store.dispatch(setCurrentClassroomToken(savedToken));
      return;
    }
  }

  if (!classrooms || currentClassroomToken?.length > 0) {
    return;
  }

  const token = classrooms && Object.keys(classrooms)[0];
  if (token) {
    store.dispatch(setCurrentClassroomToken(token));
  }
}

/**
 * Claims are custom data associated with a user via the google api
 * 
 * @param firebaseUser 
 * @param isLTI 
 * @returns 
 */
const getClaims = async (firebaseUser: firebase.User | null, isLTI: boolean): Promise<Claims> => {
  const idTokenResult = await firebaseUser?.getIdTokenResult();

  const claims = idTokenResult?.claims as Claims;
  if (claims && isLTI) {
    claims.role = getRoleFromLTIRoles(claims?.roles);
  }
  return claims;
}

/**
 *  The function that listens for and dispatches
 *  various user-server communication related actions.
 */
const asyncMiddleware = <S extends State>(
  store: MiddlewareAPI<Dispatch, S>,
  firebase: Firebase
) => (next: Dispatch) => async <A extends Action>(action: A) => {
      if (appStart.match(action)) {
        try {
          // Have the lti server generate a jwt token for the lti user
          const jwt = await getLTIAuthToken();

          // Store the ltik and ltijs url params into redux
          const searchParams = new URLSearchParams(window.location.search);
          store.dispatch(setLTIEnv({
            ltijsUrl: searchParams.get('ltijsUrl'),
            ltiToken: searchParams.get('ltik')
          }));

          await firebase!.doSignOut();
          
          const auth = await firebase.doSignInWithJWT(jwt);
          const firebaseUser = auth.user;
          const claims = await getClaims(firebaseUser, true);
          store.dispatch(updateAuthUserAction({ firebaseUser, claims }));
          store.dispatch(setLocation(UILocation.home));
        } catch (error) {}
        const unsubscribeAuthStateChange = firebase.auth.onAuthStateChanged(
          async (firebaseUser: firebase.User | null) => {
            if (firebaseUser) {
              const claims = await getClaims(firebaseUser, store.getState().app.ltiEnv);
              const authUser: AuthUser = { firebaseUser, claims };

              subscribeCurrentUser(store, firebase, authUser);
              store.dispatch(updateAuthUserAction(authUser));
            } else {
              store.dispatch(updateAuthUserAction({ firebaseUser: null }));
              store.getState().firebase.currentUserUnsubscribe();
              store.dispatch(updateCurrentUserUnsubscribe(noop));
            }
            store.dispatch(firebaseInitialized(firebase));
          }
        );

        store.dispatch(initAuthUnsubscribeAction(unsubscribeAuthStateChange));

      } else if (signOut.match(action)) {
        await firebase!.doSignOut();
        store.dispatch(setLocation(UILocation.signin));
        store.dispatch(updateCurrentUser(undefined));
      } else if (updateAuthUserAction.match(action)) {
        // This seems ugly, basically if a user is authenticated via the google api system, we want to
        // make sure the 'claims', which is extra data associated with the user, are included with it.
        const firebaseUser = action.payload?.firebaseUser;
        const claims = action.payload?.claims;

        if (firebaseUser && !claims) {
          // HACK: Don't use await, want the original updateAuthUserAction to resolve before
          // we dispatch an update
          getClaims(firebaseUser, store.getState().app.ltiEnv).then((claims) => {
            store.dispatch(updateAuthUserAction({ firebaseUser, claims }));
            store.dispatch(setLocation(UILocation.home));
          });
        }
      } else if (setToursShown.match(action)) {
        const state = store.getState();
        const currentUser = state.firebase.currentUser;
        const toursShown = action.payload;
        if (currentUser && !AreArraysEqual(currentUser.toursShown || [], toursShown, (a,b)=>a.localeCompare(b))) {
          firebase?.updateUser(currentUser, { toursShown });
        }
      } else if (setClassStartAndEndDates.match(action)) {
        const state = store.getState();
        const [startDate, endDate] = action.payload;
        firebase?.updateClassStartAndEndDates(state.app.currentClassroomToken, startDate, endDate);
      } else if (setClassAutoGrade.match(action)) {
        const state = store.getState();
        firebase?.updateClassAutoGrade(state.app.currentClassroomToken, action.payload);
      } else if (setDashboardTelemetry.match(action)) {
        const currentUser = store.getState().firebase.currentUser?.uid;
        if (currentUser) {
          firebase?.updateDashboardTelemetry(currentUser, action.payload);
        }
      } else if (setCurrentClassroomToken.match(action)) {
        const classroomToken = action.payload;
        if (classroomToken.length > 0) {
          const classroomData = await firebase?.getTeacherClassroomWithDefaults(classroomToken);
          store.dispatch(updateClassSettingsFromDb({
            endTsMs: classroomData.endTsMs,
            startTsMs: classroomData.startTsMs,
            sendGrades: classroomData.sendGrades,
            loadFromLoki: classroomData.loadFromLoki
          }));
          const cardSettings = await firebase?.getCardSettings(classroomToken);
          if (cardSettings) {
            store.dispatch(dashboardSettingsLoaded(cardSettings));
          }
        }
      } else if (studentCompletedModule.match(action) || studentSurveyClicked.match(action)) {
        const sendGrade = action.payload;
        const isSurveyClick = studentSurveyClicked.match(action);
        const state = store.getState();
        const {
          app: {
            ltiEnv,
            ltiServerUrl,
            ltiToken,
            currentClassroomSendGrade,
            currentStudentClassroomToken
          },
          statistics: { currentCompetencyId },
          firebase: { currentUser }
        } = state;

        const userId = currentUser?.uid;
        if (userId) {
          let moduleCompletionInfo = await firebase?.getModuleCompletionInfo(
            userId, currentCompetencyId, currentStudentClassroomToken);
          if (!moduleCompletionInfo) {
            moduleCompletionInfo = {
              moduleName: currentCompetencyId,
              completionTime: Date.now(),
              gradeSent: false,
              surveyComplete: isSurveyClick
            };
          }

          // Call to send lti grade
          const sentGradeAlready = moduleCompletionInfo.gradeSent;
          if (ltiEnv && sendGrade && !sentGradeAlready) {
            const progressRecorded = await sendCompletionProgress(
              1, !currentClassroomSendGrade, ltiServerUrl, ltiToken);
            if (progressRecorded) {
              moduleCompletionInfo.gradeSent = true;
            }
          }

          if (isSurveyClick) {
            moduleCompletionInfo.surveyComplete = true;
          }

          // Send save only at the end
          // TODO: Don't send if nothing changes.
          await firebase?.saveModuleCompletionInfo(
            userId,
            currentCompetencyId,
            currentStudentClassroomToken,
            moduleCompletionInfo
          );
        }
      } else if (studentProblemResult.match(action)) {
        const rawLogData = action.payload;
        if (rawLogData) {
          const state = store.getState();
          const {
            app: { currentClassroomToken, moduleToVersion },
            firebase: { currentUser }
          } = state;
          if (currentUser) {
            rawLogData.skill
            const eventDocumentId = await firebase?.addUserEvent(
              currentUser.uid, 
              currentClassroomToken,
              moduleToVersion[rawLogData.skill.id],
              rawLogData,
              Date.now()
            );

            if (rawLogData.type === SFFProblemType.SelfEval) {
              firebase?.saveSelfEvalResponses(eventDocumentId, rawLogData as RawEvalPageData, currentClassroomToken);
            }
          }
        }
      } else if (pickThenLoadExampleFromEventIds.match(action)) {
        const { cardId, eventIds, showAnswers, showUsername, tagIdsToEmphasize } = action.payload;
        const savedEventData = getSavedExampleInfoAndSave(store, firebase, cardId, eventIds);

        // From the given card id
        store.dispatch(loadAndDisplayExampleFromEventId({
          eventId: savedEventData.lastEventId,
          cardId,
          showAnswers,
          showUsername,
          tagIdsToEmphasize
        }))
      }  else if (loadAndDisplayExampleFromEventId.match(action)) {
        const { eventId } = action.payload;
        getEvent(store, firebase, eventId, true);
      } else if (generateExampleFromEventId.match(action)) {
        // WARNING: the dispatch of the exampleToDisplayGenerated must occur on the next tick, there needs to
        // be a delay because the generateExampleFromEventId will alter the same redux state as
        // exampleToDisplayGenerated. Delay ensure they update state in the correct order.
        // This is why we should avoid chaining action dispatches.
        const { eventIds, tagIds, cardId } = action.payload;
        const { lastEventId, generatorSeed } = getSavedExampleInfoAndSave(store, firebase, cardId, eventIds);
        getEvent(store, firebase, lastEventId, false, (data) => {
          const problemTypesThatCanBeGenerated = [
            SFFProblemType.Type1,
            SFFProblemType.Type2,
            SFFProblemType.Type3,
            SFFProblemType.Type4,
            SFFProblemType.Type6,
            SFFProblemType.Discourse
          ];

          // Only certain problem types can be generated
          if (problemTypesThatCanBeGenerated.indexOf(data.type) >= 0) {
            // Use the given event data as a template to generate a new example question
            const skill = data.skill.id;
            const questionType = data.type;

            let ignoreList: string[] = [];
            if (lastEventId && lastEventId.length > 0) {
              const { eventIdToEvent } = store.getState().statistics;
              const eventDataToIgnore = eventIdToEvent[lastEventId];
              if (eventDataToIgnore) {
                ignoreList = eventDataToIgnore.ignoreList
              }
            }

            // Once question is generated, it needs to be added to redux state as the data to display
            ProblemGenerator.getProblem({
              skill,
              ignore: ignoreList,
              level: { main: 0, secondary: 0 },
              type: questionType,
              requiredCorrectTag: (tagIds.length > 0) ? tagIds[0] : undefined,
              requiredIncorrectTag: (tagIds.length > 1) ? tagIds[1] : undefined,
              seed: generatorSeed
            }).then((generatedProblem) => {
              // If couldn't generate a problem, swap incorrect/correct tag if they were specified
              if (!generatedProblem && tagIds.length > 1) {
                ProblemGenerator.getProblem({
                  skill,
                  ignore: ignoreList,
                  level: { main: 0, secondary: 0 },
                  type: questionType,
                  requiredCorrectTag: tagIds[1],
                  requiredIncorrectTag: tagIds[0],
                  seed: generatorSeed
                }).then((generatedProblem) => {
                  // If unable to generate a problem, just use the user event data
                  store.dispatch(exampleToDisplayGenerated(generatedProblem ?? data));
                });
              } else {
                // If unable to generate a problem, just use the user event data
                store.dispatch(exampleToDisplayGenerated(generatedProblem ?? data));
              }
            });
          } else {
            // For self-eval it picks the same example (gets stuck on loading)
            setTimeout(() => store.dispatch(exampleToDisplayGenerated(data)), 5);
          }
        });
      } else if (updateRandomExampleSettingsForCardId.match(action)) {
        // When updating random setting, look at cached context to figure out if we are generating a new
        // example from scratch or just picking a new user mistake
        const { teacherUI: {
          cachedGeneratedExampleParams: {
            cardId,
            eventIds,
            isShowingUserExample,
            tagIds,
            showUsername
          }
        }} = store.getState();

        if (isShowingUserExample) {
          // Pick a different user example.
          const prevEntry = getSavedExampleInfo(store, cardId, eventIds);
          const candidates = eventIds.filter(value => value !== prevEntry.lastEventId);
          getSavedExampleInfoAndSave(store, firebase, cardId, eventIds, undefined, randomElement(candidates));
          store.dispatch(pickThenLoadExampleFromEventIds({
            cardId,
            eventIds,
            showAnswers: true,
            showUsername
          }));
        } else {
          // Generate new seed
          getSavedExampleInfoAndSave(store, firebase, cardId, eventIds, Date.now(), undefined);
          store.dispatch(generateExampleFromEventId({
            cardId,
            eventIds,
            tagIds
          }));
        }
      } else if (loadAllEventsForUser.match(action)) {
        const state = store.getState();
        const {
          app: { currentClassroomToken },
          statistics: { currentCompetencyId }
        } = state;
        if (currentCompetencyId) {
          const results = await firebase?.getAllEventsForUserInSkill(
            action.payload, currentCompetencyId, currentClassroomToken);
          store.dispatch(addNewEvents(results))
        }
      } else if (getModuleCompletionForUser.match(action)) {
        const userid = action.payload;
        const {
          app: { currentClassroomToken },
          statistics: { currentCompetencyId }
        } = store.getState();
        const moduleInfo = await firebase?.getModuleCompletionInfo(
          userid, currentCompetencyId, currentClassroomToken);
        if (moduleInfo) {
          store.dispatch(setModuleCompletionForUser({data: moduleInfo, userId: userid}));
        }
      } else if (loadStudentDataForDashboard.match(action)) {
        const { skillId, useFirebase, isInitialData, cardType, loadFirebaseEvents } = action.payload;

        // Treating this as teacher dashboard initial load.
        // Use this as opportunity to also make sure classroom details (like instructor name)
        // is attached to firestore doc for class
        const {
          app: { currentClassroomToken, ltiEnv }
        } = store.getState();
        if (ltiEnv) {
          const classContext = await getClassRoomContext();
          if (classContext && classContext.name) {
            firebase?.addInstructorNameToClass(currentClassroomToken, classContext.name);
          }
        }

        if (useFirebase) {
          const state = store.getState();
          const {
            app: { currentClassroomToken, moduleToVersion }
          } = state;
          let skillDescriptions;
          let skillIdToTags;
          if (isInitialData) {
            const fixedDescriptions = await ProblemGenerator.getFixedDescriptionData();
            skillDescriptions = fixedDescriptions.skillDescriptions;
            skillIdToTags = fixedDescriptions.tagDescriptions;
          }

          // Load either cards or the raw events
          let loggedEvents;
          let cards;
          if (loadFirebaseEvents) {
            loggedEvents = await firebase?.getAllEventsForSkill(skillId, currentClassroomToken);
          } else {
            // If cardType not defined load all cards
            let firebaseCardType: FirebaseCardType | undefined = undefined;
            if (cardType) {
              firebaseCardType = (cardType === 'identifyIssues') ? 'Issue' : 'Confusion';
            }
            cards = await firebase?.getCardsByTypeAndSkill(
              currentClassroomToken,
              skillId,
              firebaseCardType,
              (skillId) ? moduleToVersion[skillId] : undefined
            );
          }
          
          store.dispatch(addNewResults({
            results: loggedEvents,
            cardResults: cards,
            lastLogTime: Date.now(),
            skillDescriptions,
            skillIdToTags
          }));
        }
      } else if (setCurrentCompetencyId.match(action)) {
        const skillIdToChangeTo = action.payload;
        const {
          app: { useFirebaseForEvents, currentCardType },
          statistics: { skillIdsPresent }
        } = store.getState();
        // Refetch data for a particular skill if we did not already fetch it before OR
        // if we requested to fetch everything.
        if (!skillIdToChangeTo || !skillIdsPresent[skillIdToChangeTo]) {
          store.dispatch(loadStudentDataForDashboard({
            isInitialData: false,
            skillId: skillIdToChangeTo,
            cardType: currentCardType,
            useFirebase: useFirebaseForEvents
          }));
        }
      } else if (setCardType.match(action)) {
        // TODO: make this smarter so it doesn't reload everything all the time
        const cardTypeToChangeTo = action.payload;
        const {
          app: { useFirebaseForEvents },
          statistics: { currentCompetencyId }
        } = store.getState();
        store.dispatch(loadStudentDataForDashboard({
          isInitialData: false,
          skillId: currentCompetencyId,
          cardType: cardTypeToChangeTo,
          useFirebase: useFirebaseForEvents
        }));
      } else if (saveCardSettings.match(action)) {
        const newSettings = action.payload;
        const {
          app: { currentClassroomToken }
        } = store.getState();
        firebase?.saveCardSettings(currentClassroomToken, newSettings, 'cardDisplay');
      } else if (loadMoreSelfEvalResponse.match(action)) {
        const selfEvalResponses = await firebase?.getSelfEvalResponses();
        store.dispatch(loadedSelfEvalResponses(selfEvalResponses));
      } else if (updateSelfEvalResponse.match(action)) {
        const { id, status } = action.payload;
        firebase?.updateSelfEvalResponse(id, status);
      } else if (launchBugReport.match(action)) {
        const bugReports = await firebase?.getBugReports(20);
        store.dispatch(loadedBugReports(bugReports));
      } else if (updateBugReport.match(action)) {
        const { id, status } = action.payload;
        await firebase?.updateBugReportStatus(id, status);
      } else if (resetProgressForModule.match(action)) {
        const { module } = action.payload;
        if (module) {
          const state = store.getState();
          const {
            app: { currentClassroomToken, moduleToVersion }
          } = state;
          const prevVersion = moduleToVersion[module] ?? "1";
          const newVersion = (parseInt(prevVersion) + 1).toString();
          firebase?.saveModuleVersion(currentClassroomToken, module, newVersion);
          action.payload.version = newVersion;
        }
      }

      // (boilerplate) Call the next dispatch method in the middleware chain.
      const returnValue = next(action);

      // (boilerplate) This will likely be the action itself, unless
      // a middleware further in chain changed it.
      return returnValue;
    };
/**
 * Have helper function to just get saved data
 */
const getSavedExampleInfo = (
  store: MiddlewareAPI<Dispatch, State>,
  cardId: string,
  eventIds: string[],
  useDefaultIfNone = true
) => {
  // Check if saved event data exists for given card id.
  // If yes, use it when picking/generating the event to display.
  // If no, generate that data, make sure it is saved to both firebase and the local redux store,
  // and use that data.
  const state = store.getState();
  const { cardIdToSelectionInfo } = state.teacherUI;
  let savedEventData = cardIdToSelectionInfo[cardId];
  if (!savedEventData && useDefaultIfNone) {
    savedEventData = {
      generatorSeed: 1,
      lastEventId: randomElement(eventIds)
    };
  }
  return savedEventData;
}

/**
 * Helper to get saved example info from the store and save new info
 */
const getSavedExampleInfoAndSave = (
  store: MiddlewareAPI<Dispatch, State>,
  firebase: Firebase,
  cardId: string,
  eventIds: string[],
  newSeed?: number,
  newPickedEventId?: string
) => {
  // Check if saved event data exists for given card id.
  // If yes, use it when picking/generating the event to display.
  // If no, generate that data, make sure it is saved to both firebase and the local redux store,
  // and use that data.
  const state = store.getState();
  let savedEventData = getSavedExampleInfo(store, cardId, eventIds, false);
  if (!savedEventData || newSeed || newPickedEventId) {
    savedEventData = {
      generatorSeed: newSeed ??
        (savedEventData ? savedEventData.generatorSeed : 1),
      lastEventId: newPickedEventId ??
        (savedEventData ? savedEventData.lastEventId : randomElement(eventIds))
    };
    store.dispatch(saveNewExampleSelectionInformation({
      ...savedEventData,
      cardId: cardId
    }));
    
    firebase.saveCardSettings(state.app.currentClassroomToken, { [cardId]: savedEventData }, 'cards');
  }
  return savedEventData;
};

const getEvent = (
  store: MiddlewareAPI<Dispatch, State>,
  firebase: Firebase,
  eventId: string,
  displayImmediately: boolean,
  callback?: (data: RawPageData) => void) => {
  const state = store.getState();
  const {
    app: { currentClassroomToken },
    statistics: { eventIdToEvent }
  } = state;
  if (!eventIdToEvent[eventId]) {
    firebase?.getSpecificUserEvent(eventId, currentClassroomToken).then((value) => {
      // Save this event back to redux (don't use await to prevent timing block)
      store.dispatch(addNewEventForExample({data: value, displayImmediately }));

      if (callback)
        callback(value.data);
    });
  } else if (callback) {
    callback(eventIdToEvent[eventId]);
  }
}

/**
 * Create middleware that handles actions related to
 * the communication b/w the server and the client as it
 * pertains to the user themselves.
 * @param store Redux store
 */
export const createFirebaseMiddleware = (
  store: MiddlewareAPI<Dispatch, State>
) => {
  const firebase = FIREBASE;
  // Return the function that listens for and dispatches actions
  // relevant to the embedding API.
  return asyncMiddleware(store, firebase);
};
